import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';

import ImageItem from './ImageItem';
import MarqueeSelectWrapper from './MarqueeSelectWrapper';

const ImagesList = ({
    content,
    isCarousel,
    url,
    title,
    loading,
    small = false,
    seeAll = false,
    handleSelection,
    showSelectButton,
    selectedItems,
}) => {
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const [hideNavigation, setHideNavigation] = useState(false);
    const [swiper, setSwiper] = useState(null);

    const getCurrentSlidesPerView = () => {
        const width = window.innerWidth;
        if (width >= 1024) return small ? 4.3 : 5.3;
        if (width >= 900) return small ? 3.8 : 4.5;
        if (width >= 768) return 3.5;
        return 2.3;
    };

    const checkNavigationVisibility = () => {
        if (content && Array.isArray(content)) {
            const currentSlidesPerView = getCurrentSlidesPerView();
            setHideNavigation(content.length <= Math.floor(currentSlidesPerView));
        } else {
            setHideNavigation(true);
        }
    };

    useEffect(
        () => {
            if (isCarousel) {
                const handleResize = () => {
                    checkNavigationVisibility();
                };

                window.addEventListener('resize', handleResize);
                checkNavigationVisibility();

                return () => {
                    window.removeEventListener('resize', handleResize);
                };
            }
        },
        [content, small],
        isCarousel
    );

    useEffect(() => {
        if (isCarousel) {
            if (!swiper || !prevButtonRef.current || !nextButtonRef.current) return;

            swiper.params.navigation.prevEl = prevButtonRef.current;
            swiper.params.navigation.nextEl = nextButtonRef.current;
            swiper.params.navigation.disabledClass = 'opacity-30 hover:opacity-30';

            swiper.navigation.init();
            swiper.navigation.update();

            swiper.on('slideChange', () => {
                setTimeout(() => {
                    swiper.navigation.update();
                }, 10);
            });
        }
    }, [swiper, prevButtonRef.current, nextButtonRef.current, isCarousel]);

    return (
        <div className="my-10">
            {title &&
                (seeAll ? (
                    <div className={`${small ? 'mb-4' : 'mb-8'} flex justify-between items-center gap-4 md:gap-10 whitespace-nowrap`}>
                        <h3 className={`text-xl ${small ? 'lg:text-2xl' : 'lg:text-4xl'} text-secondary font-bold`}>{title}</h3>

                        <div className="w-full border border-white opacity-20" />

                        <Link
                            to={url}
                            className="text-white font-semibold max-sm:text-xs md:tracking-widest uppercase px-3 md:px-5 py-1.5 md:py-2 border-2 border-secondary rounded-lg transition-all hover:bg-secondary"
                        >
                            Ver Tudo
                        </Link>
                    </div>
                ) : (
                    <h3
                        className={`text-2xl max-lg:mb-4 ${small ? 'xl:text-2xl lg:mb-3' : 'xl:text-4xl lg:mb-8'} text-secondary font-bold`}
                    >
                        {title}
                    </h3>
                ))}

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20">
                    {[...Array(4)].map((_, index) => (
                        <div
                            key={index}
                            role="status"
                            className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                    ))}
                </div>
            ) : isCarousel ? (
                <div className="relative">
                    <Swiper
                        spaceBetween={20}
                        slidesPerView={6.3}
                        modules={[Navigation]}
                        onSwiper={(swiperInstance) => {
                            setSwiper(swiperInstance);
                        }}
                        breakpoints={{
                            1024: { slidesPerView: 6.3, spaceBetween: 20 },
                            900: { slidesPerView: 5.3, spaceBetween: 15 },
                            768: { slidesPerView: 4.3 },
                            0: { slidesPerView: 3.3, spaceBetween: 12 },
                        }}
                    >
                        {content.map((slide, index) => (
                            <SwiperSlide key={index}>
                                <ImageItem
                                    slides={content}
                                    slide={slide}
                                    handleSelection={handleSelection}
                                    selectedItems={selectedItems}
                                    showSelectButton={showSelectButton}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <button
                        ref={prevButtonRef}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:left-3 before:w-3 before:h-3 before:border-t-2 before:border-l-2 before:-rotate-45"
                        aria-label="Previous slide"
                    ></button>

                    <button
                        ref={nextButtonRef}
                        className="absolute -right-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:right-3 before:w-3 before:h-3 before:border-b-2 before:border-r-2 before:-rotate-45"
                        aria-label="Next slide"
                    ></button>
                </div>
            ) : showSelectButton ? (
                <MarqueeSelectWrapper
                    onSelect={(selectedIds) => {
                        selectedIds.forEach((id) => handleSelection(parseInt(id)));
                    }}
                >
                    <div className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-8 gap-3 xl:gap-8">
                        {content.map((slide, index) => (
                            <div key={index} className="selectable-item" data-id={slide.id}>
                                <ImageItem
                                    slides={content}
                                    slide={slide}
                                    handleSelection={handleSelection}
                                    selectedItems={selectedItems}
                                    showSelectButton={showSelectButton}
                                />
                            </div>
                        ))}
                    </div>
                </MarqueeSelectWrapper>
            ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-8 gap-3 xl:gap-8">
                    {content &&
                        content.map((slide, index) => (
                            <div key={index}>
                                <ImageItem
                                    slides={content}
                                    slide={slide}
                                    handleSelection={handleSelection}
                                    selectedItems={selectedItems}
                                    showSelectButton={showSelectButton}
                                />
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default ImagesList;
